import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSnackbar } from 'notistack';
import CustomTable from '../../components/CustomTable';
import SearchBar from '../../components/search.component';
import SectionDetail from './section-details';
import SectionForm from './section-form';
import SectionPanel from './SectionPanel';
import DeleteConfirmDialog from '../../components/deleteConfirmation.component';
import apiRequest from '../../services/apiClient';
import { formatDate } from '../../utils/utils';
import { invalidateCache } from '../../components/serverAutocomplete.component';

const Sections = ({ setTitleProps }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [sections, setSections] = useState({ items: [], totalCount: 0 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const [filters, setFilters] = useState({ searchQuery: '' });
  const [selectedItem, setSelectedItem] = useState();
  const [showForm, setShowForm] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isExecutingRef = useRef(false);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    setTitleProps("Sections");
  }, []);

  useEffect(() => {
    if (isExecutingRef.current) return;
    isExecutingRef.current = true;

    const loadSections = async () => {
      setLoading(true);
      const order = sortModel && sortModel.length > 0
        ? `&$orderby=${sortModel[0].field} ${sortModel[0].sort}`
        : "";

      const searchQueryFilter = filters?.searchQuery && filters.searchQuery.length > 0 && filters.searchQuery.trim().length > 0
        ? `contains(name,'${filters.searchQuery}')`
        : "";

      let filter = searchQueryFilter;
      
      if (filter && filter.length > 0)
        filter = "&$filter=" + filter;

      try {
        const response = await apiRequest(`section/paged?$select=id,created,name,backgroundColor,fontColor&$top=${pageSize}&$skip=${page*pageSize}${order}${filter}`);
        setSections({
          items: response.data.items || response.data,
          totalCount: response.data.totalCount || response.totalCount || 0
        });
      } catch (error) {
        enqueueSnackbar('Error loading sections', { variant: 'error' });
      } finally {
        setLoading(false);
        isExecutingRef.current = false;
      }
    };

    loadSections();
  }, [page, pageSize, sortModel, filters.searchQuery]);

  const onHandlePageSizeChange = (newSize) => {
    setPageSize(newSize);
  };

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel);
  };

  const handleOnSearchQueryFilterChange = (searchQuery) => {
    setFilters({ ...filters, searchQuery });
  };

  const handleRowSelection = useCallback((e) => {
    const newSelectedItem = e[0];
    // Solo actualizar si es diferente al valor actual
    if (selectedItemRef.current !== newSelectedItem) {
      selectedItemRef.current = newSelectedItem;
      setSelectedItem(newSelectedItem);
    }
  }, []);

  const handleUnselectItem = useCallback(() => {
    selectedItemRef.current = null;
    setSelectedItem(null);
  }, []);

  const handleDeleteConfirmation = useCallback((itemId) => {
    setDeleteItemId(itemId);
    setConfirmOpen(true);
  }, []);

  const executeLoadSections = async () => {
    if (isExecutingRef.current) return;
    isExecutingRef.current = true;

    try {
      const loadSections = async () => {
        setLoading(true);
        const order = sortModel && sortModel.length > 0
          ? `&$orderby=${sortModel[0].field} ${sortModel[0].sort}`
          : "";

        const searchQueryFilter = filters?.searchQuery && filters.searchQuery.length > 0 && filters.searchQuery.trim().length > 0
          ? `contains(name,'${filters.searchQuery}')`
          : "";

        let filter = searchQueryFilter;
        
        if (filter && filter.length > 0)
          filter = "&$filter=" + filter;

        const response = await apiRequest(`section/paged?$select=id,created,name,backgroundColor,fontColor&$top=${pageSize}&$skip=${page*pageSize}${order}${filter}`);
        setSections({
          items: response.data.items || response.data,
          totalCount: response.data.totalCount || response.totalCount || 0
        });
        setLoading(false);
      };

      await loadSections();
    } finally {
      isExecutingRef.current = false;
    }
  };

  const handleDelete = async (itemId) => {
    setLoading(true);
    try {
      await apiRequest(`section/${itemId}`, { method: "DELETE" });
      enqueueSnackbar('Section successfully deleted!', { variant: 'success' });
      executeLoadSections();
      setConfirmOpen(false);
      handleUnselectItem(); // Cerrar el panel de detalles después de eliminar
      // Invalidar caché de sections para refrescar las listas
      invalidateCache('section');
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message ?? "An unknown error occurred", { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionAdd = useCallback(() => {
    enqueueSnackbar('Section successfully added!', { variant: 'success' });
    setShowForm(false);
    executeLoadSections();
  }, [enqueueSnackbar]);

  const handleEditItem = useCallback(() => {
    setShowForm(true);
  }, []);

  const handleDeleteItem = useCallback(() => {
    handleDeleteConfirmation(selectedItem);
  }, [selectedItem, handleDeleteConfirmation]);

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 200,
      sortable: true
    },
    {
      field: 'backgroundColor',
      headerName: 'Background Color',
      minWidth: 150,
      valueGetter: (params) => (
        <div className="flex items-center">
          <div 
            className="w-4 h-4 rounded mr-2 border border-gray-300"
            style={{ backgroundColor: params.value || '#ffffff' }}
          />
          <span className="text-sm">{params.value || '#ffffff'}</span>
        </div>
      ),
      sortable: false
    },
    {
      field: 'fontColor',
      headerName: 'Font Color',
      minWidth: 150,
      valueGetter: (params) => (
        <div className="flex items-center">
          <div 
            className="w-4 h-4 rounded mr-2 border border-gray-300"
            style={{ backgroundColor: params.value || '#000000' }}
          />
          <span className="text-sm">{params.value || '#000000'}</span>
        </div>
      ),
      sortable: false
    },
    {
      field: 'created',
      headerName: 'Created',
      minWidth: 170,
      valueGetter: (params) => {
        return formatDate(params.value);
      },
      sortable: true
    }
  ], []);

  const memoizedSectionPanel = useMemo(() => {
    if (!selectedItem && !showForm) return null;
    
    return (
      <SectionPanel
        selectedItem={selectedItem}
        showForm={showForm}
        handleUnselectItem={handleUnselectItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        handleDeleteConfirmation={handleDeleteConfirmation}
        handleSectionAdd={handleSectionAdd}
        setShowForm={setShowForm}
      />
    );
  }, [selectedItem, showForm, handleUnselectItem, handleEditItem, handleDeleteItem, handleDeleteConfirmation, handleSectionAdd, setShowForm]);

  return (
    <div className="p-6">
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => handleDelete(deleteItemId)}
        title="Delete Section?"
      >
        Are you sure you want to delete this section?
      </DeleteConfirmDialog>

      {/* Header with Add button and Search */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          </div>
          
          <div className="max-w-md">
            <SearchBar onTextChange={handleOnSearchQueryFilterChange} disabled={loading} />
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomTable
        rows={sections.items}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        onPageSizeChange={onHandlePageSizeChange}
        page={page}
        onPageChange={(newPage) => setPage(newPage)}
        totalCount={sections.totalCount}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowSelection}
        selectedRow={selectedItem}
        onView={(row) => {
          const newSelectedItem = row.id;
          if (selectedItemRef.current !== newSelectedItem) {
            selectedItemRef.current = newSelectedItem;
            setSelectedItem(newSelectedItem);
          }
        }}
        onEdit={(row) => {
          const newSelectedItem = row.id;
          if (selectedItemRef.current !== newSelectedItem) {
            selectedItemRef.current = newSelectedItem;
            setSelectedItem(newSelectedItem);
          }
          setShowForm(true);
        }}
        onDelete={(row) => handleDeleteConfirmation(row.id)}
      />

      {/* Side Panel */}
      {(selectedItem || showForm) && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:right-0 sm:top-0 sm:h-full sm:w-96">
          {/* Mobile: Full screen overlay */}
          <div className="sm:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white shadow-xl w-full h-full flex flex-col">
              <div className="p-6 pb-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {showForm ? (selectedItem ? 'Edit Section' : 'Add Section') : 'Section Details'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      handleUnselectItem();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                {memoizedSectionPanel}
              </div>
            </div>
          </div>
          
          {/* Desktop: Drawer lateral derecho */}
          <div className="hidden sm:block fixed right-0 top-0 h-full w-96 bg-white shadow-xl">
            <div className="flex flex-col h-full">
              <div className="p-6 pb-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {showForm ? (selectedItem ? 'Edit Section' : 'Add Section') : 'Section Details'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      handleUnselectItem();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {memoizedSectionPanel}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => handleDelete(deleteItemId)}
        title="Delete Section"
      >
        Are you sure you want to delete this section? This action cannot be undone.
      </DeleteConfirmDialog>
    </div>
  );
};

export default Sections;